import { baseApi } from "@/shared/api";
import type { ProjectPublication, ProjectPublicationPayload } from "../types";

interface ProjectScopedArg {
  projectId: number;
}

interface PublicationArg extends ProjectScopedArg {
  publicationId: number;
}

interface CreatePublicationArg extends ProjectScopedArg {
  body: ProjectPublicationPayload;
}

interface UpdatePublicationArg extends PublicationArg {
  body: Partial<ProjectPublicationPayload>;
}

const TEXT_FIELDS = [
  "title",
  "publication_date",
  "publication_type",
  "description",
  "url",
  "source_name",
  "author",
  "language",
] as const;

function needsFormData(body: Partial<ProjectPublicationPayload>): boolean {
  return (
    body.picture instanceof File ||
    body.file instanceof File ||
    body.remove_picture === true ||
    body.remove_file === true
  );
}

function buildFormData(body: Partial<ProjectPublicationPayload>): FormData {
  const formData = new FormData();

  TEXT_FIELDS.forEach(function appendText(field) {
    const value = body[field];
    if (value !== undefined && value !== null && value !== "") {
      formData.append(field, String(value));
    }
  });

  if (body.reach !== undefined && body.reach !== null) {
    formData.append("reach", String(body.reach));
  }
  if (body.event !== undefined && body.event !== null) {
    formData.append("event", String(body.event));
  }
  if (body.picture instanceof File) {
    formData.append("picture", body.picture);
  }
  if (body.file instanceof File) {
    formData.append("file", body.file);
  }
  if (body.remove_picture) {
    formData.append("remove_picture", "true");
  }
  if (body.remove_file) {
    formData.append("remove_file", "true");
  }

  return formData;
}

function buildJsonBody(body: Partial<ProjectPublicationPayload>) {
  const {
    picture: _picture,
    file: _file,
    remove_picture: _removePicture,
    remove_file: _removeFile,
    ...jsonBody
  } = body;
  return jsonBody;
}

export const publicationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjectPublications: builder.query<ProjectPublication[], ProjectScopedArg>({
      query: ({ projectId }) => ({
        url: `/api/projects/${projectId}/publications/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: "ProjectPublications", id: projectId },
      ],
    }),
    getProjectPublication: builder.query<ProjectPublication, PublicationArg>({
      query: ({ projectId, publicationId }) => ({
        url: `/api/projects/${projectId}/publications/${publicationId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { publicationId }) => [
        { type: "ProjectPublications", id: `PUBLICATION-${publicationId}` },
      ],
    }),
    createProjectPublication: builder.mutation<ProjectPublication, CreatePublicationArg>({
      query: ({ projectId, body }) => ({
        url: `/api/projects/${projectId}/publications/`,
        method: "POST",
        body: needsFormData(body) ? buildFormData(body) : buildJsonBody(body),
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "ProjectPublications", id: projectId },
      ],
    }),
    updateProjectPublication: builder.mutation<ProjectPublication, UpdatePublicationArg>({
      query: ({ projectId, publicationId, body }) => ({
        url: `/api/projects/${projectId}/publications/${publicationId}/`,
        method: "PATCH",
        body: needsFormData(body) ? buildFormData(body) : buildJsonBody(body),
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId, publicationId }) => [
        { type: "ProjectPublications", id: projectId },
        { type: "ProjectPublications", id: `PUBLICATION-${publicationId}` },
      ],
    }),
    deleteProjectPublication: builder.mutation<void, PublicationArg>({
      query: ({ projectId, publicationId }) => ({
        url: `/api/projects/${projectId}/publications/${publicationId}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "ProjectPublications", id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectPublicationsQuery,
  useGetProjectPublicationQuery,
  useCreateProjectPublicationMutation,
  useUpdateProjectPublicationMutation,
  useDeleteProjectPublicationMutation,
} = publicationsApi;
